<script>
    import { environment } from "@app/store/envStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { modalState, showModal, hideModal } from "@app/store/modalStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { fade } from "svelte/transition";
    import { v4 as uuidv4 } from "uuid";

    import tcLib from "@cclib/lib/timecode.js";
    import { knpState, loadKnpGuides } from "@app/store/knpStore.svelte.js";

    /* Firebase */
    import firebase from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import storage from "@app/configs/storage.js";

    /* Language Settings */
    import assemblyAiLanguages from "@cclib/providers/assemblyAi/languages.js";
    import deepgramLanguages from "@cclib/providers/deepgram/languages.js";
    import revAiLanguages from "@cclib/providers/revAi/languages.js";
    import googleLanguages from "@cclib/providers/googleSpeechToText/languages.js";
    import speechmaticsLanguages from "@cclib/providers/speechmatics/languages.js";
    import voiceGainLanguages from "@cclib/providers/voicegain/languages.js";
    import elevenLabsScribeLanguages from "@cclib/providers/elevenLabsScribe/languages.js";
    import * as m from '@app/paraglide/messages.js';

    let mediaDuration;
    let jobInfo;
    let statusMsg = $state(m.uploading_please_wait());

    let transcriptionDefaults =
        JSON.parse(localStorage.getItem("cc-transcription-defaults")) || {};

    let provider = $state(transcriptionDefaults.provider || "Speechmatics"),
        language = $state(transcriptionDefaults.language || "en"),
        selectedKnpGuide = $state(transcriptionDefaults.knpGuideId || null),
        interactionType = "PROFESSIONALLY_PRODUCED",
        speakers = 2,
        uploadTask,
        uploading = $state(false),
        progress = $state(0),
        userId = firebase.auth().currentUser.uid,
        userEmail = firebase.auth().currentUser.email,
        teamId = authState.team ? authState.team.id : null,
        homeRef = authState.team
            ? db.collection("teams").doc(teamId).collection("jobs")
            : db.collection("users").doc(userId).collection("jobs"),
        storageRef = storage.ref(),
        uploadBasePath = authState.team
            ? "teams/" + teamId + "/watch/video/"
            : "users/" + userId + "/uploads/",
        audioUploadBasePath = authState.team
            ? "teams/" + teamId + "/watch/audio/"
            : "users/" + userId + "/audio/",
        input,
        output;

    /* Speechmatics */
    let trgLanguages = $state([]),
        domain = $state(transcriptionDefaults.domain || "none"),
        locale = $state(transcriptionDefaults.locale || "en-US"),
        enableTranslation = $state(false);

    function extractAudio() {
        try {
            const os = window.os;
            const path = window.path;
            const ffmpeg = window.ffmpeg;
            console.log("EXTRACTING AUDIO");
            uploading = true;
            progress = 0;
            input = projectState.media.localPath;
            output = os.tmpdir() + path.sep + projectState.id + ".wav";

            toast.push(m.audio_extraction_in_progress(), {
                classes: ["toast-info"],
            });

            ffmpeg(input)
                .audioCodec("pcm_mulaw")
                .audioChannels(1)
                .audioFrequency(44100)
                .output(output)
                .on("start", function (commandLine) {
                    console.log("Spawned Ffmpeg with command: " + commandLine);
                })
                .on("codecData", function (data) {
                    console.log(data.duration);
                    mediaDuration = data.duration;
                    console.log(
                        "Input is " +
                            data.audio +
                            " audio " +
                            "with " +
                            data.video +
                            " video",
                    );
                })
                .on("progress", function (progress) {
                    /* progress = {percent, timemark, currentFps, frames, targetSize, currentKbps} */
                    console.log("Extracting Audio: " + progress.timemark + " done");
                })
                .on("end", function () {
                    toast.push(m.audio_extraction_complete_uploading(), {
                        classes: ["toast-success"],
                    });

                    submitJob();
                })
                .on("error", function (err, stdout, stderr) {
                    console.log(err, err.message);
                    toast.push(
                        m.failed_to_extract_audio_from_source_video({ error: err.message }),
                        {
                            classes: ["toast-danger"],
                        },
                    );
                })
                .run();
        } catch (err) {
            console.log(err, err.message);
        }
    }

    async function submitJob() {
        /* 
        1. Register the job 
        2. Upload File
        3. If Failure - update job and clean up.
    */
        console.log("Submitting Automatic Transcription Job");

        localStorage.setItem(
            "cc-transcription-defaults",
            JSON.stringify({
                provider: provider,
                language: language,
                domain: domain,
                locale: locale,
                knpGuideId: selectedKnpGuide,
            }),
        );

        let jobRegistration = false;
        uploading = true;
        progress = 5;

        jobInfo = {
            id: uuidv4(),
            mediaSource: projectState.media.storage,
            projectId: projectState.id,
            projectName: projectState.name,
            submittedBy: userEmail,
            userId: userId,
            extId: null,
            progress: 0,
            duration: mediaDuration ? tcLib.tcMsToSec(mediaDuration) : 0,
            cost: 0,
            type: "transcription",
            statusMsg: "Awaiting Media",
            status: "Submitted",
            createdOn: firebase.firestore.Timestamp.fromDate(new Date()),
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date()),
            completedOn: null,
            deleted: false,
            config: {
                provider: provider,
                language: language,
                speakers: speakers,
                interactionType: interactionType,
                domain: domain,
                locale: locale,
                enableTranslation: enableTranslation,
                trgLanguages: trgLanguages.slice(0, 5).join(),
                knpGuideId: selectedKnpGuide,
            },
        };

        if (projectState.media.storage === "Cloud Storage") {
            homeRef
                .doc(jobInfo.id)
                .set(jobInfo)
                .then(() => {
                    console.log("Transcribe job saved successfully", jobInfo);
                    jobRegistration = true;
                    firebase
                        .functions()
                        .httpsCallable("v8SubmitCloudTranscribeJob")({
                        jobId: jobInfo.id,
                        mediaUrl: projectState.media.path,
                    });

                    toast.push(m.transcription_job_submitted_success(), {
                        classes: ["toast-success"],
                    });

                    showModal("aiTranscriptImport");
                })
                .catch((error) => {
                    console.log("Error submitting transcription job: ", error);

                    toast.push(m.transcription_error({ error: error.message }), {
                        classes: ["toast-danger"],
                    });

                    /* Update Job Record */
                    if (jobRegistration) {
                        homeRef.doc(jobInfo.id).update({
                            progress: 100,
                            statusMsg:
                                "Failed submission process. " + error.message,
                            status: "Failed",
                            completedOn: firebase.firestore.Timestamp.fromDate(
                                new Date(),
                            ),
                            updatedOn: firebase.firestore.Timestamp.fromDate(
                                new Date(),
                            ),
                        });
                    }

                    uploading = false;
                    progress = 0;
                });
        } else if (projectState.media.storage === "YouTube") {
            // Use Firebase Functions for YouTube downloads (both Electron and web)
            homeRef
                .doc(jobInfo.id)
                .set(jobInfo)
                .then(() => {
                    console.log("Transcribe job saved successfully", jobInfo);
                    jobRegistration = true;
                    return firebase
                        .functions()
                        .httpsCallable("v8SubmitYouTubeTranscribeJob")({
                        jobId: jobInfo.id,
                        mediaUrl: projectState.media.path,
                    });
                })
                .then(() => {
                    toast.push(
                        m.transcription_job_submitted_success(),
                        {
                            classes: ["toast-success"],
                        },
                    );

                    showModal("aiTranscriptImport");
                })
                .catch((error) => {
                    console.log("Error submitting transcription job: ", error);

                    toast.push(m.transcription_error({ error: error.message }), {
                        classes: ["toast-danger"],
                    });

                    /* Update Job Record */
                    if (jobRegistration) {
                        homeRef.doc(jobInfo.id).update({
                            progress: 100,
                            statusMsg:
                                "Failed submission process. " + error.message,
                            status: "Failed",
                            completedOn:
                                firebase.firestore.Timestamp.fromDate(
                                    new Date(),
                                ),
                            updatedOn:
                                firebase.firestore.Timestamp.fromDate(
                                    new Date(),
                                ),
                        });
                    }

                    uploading = false;
                    progress = 0;
                });
        } else if (
            projectState.media.storage === "Local Storage" ||
            projectState.media.storage === "Proxy RT"
        ) {
            homeRef
                .doc(jobInfo.id)
                .set(jobInfo)
                .then(() => {
                    console.log("Transcribe job saved successfully", jobInfo);
                    jobRegistration = true;
                    if (environment.electron) {
                        console.log("Fetching local output from audio extraction");
                        return fetch(output)
                    } else {
                        return fetch(projectState.media.path);
                    }
                })
                .then((fileResponse) => {
                    return fileResponse.arrayBuffer();
                })
                .then((byteArrayFileRes) => {
                    //console.log(byteArrayFileRes);
                    uploadTask = storageRef
                        .child(
                            (environment.electron
                                ? audioUploadBasePath
                                : uploadBasePath) + jobInfo.id,
                        )
                        .put(byteArrayFileRes);
                    uploadTask.on(
                        "state_changed",
                        (snapshot) => {
                            //console.log(snapshot);
                            progress = (
                                (snapshot.bytesTransferred /
                                    snapshot.totalBytes) *
                                100
                            ).toFixed(2);
                        },
                        (error) => {
                            console.log(error, error.code);

                            toast.push(
                                m.transcription_error({ error: error.message }),
                                {
                                    classes: ["toast-danger"],
                                },
                            );

                            /* Update Job Record */
                            if (jobRegistration) {
                                homeRef.doc(jobInfo.id).update({
                                    progress: 100,
                                    statusMsg: error.code,
                                    status: "Failed",
                                    completedOn:
                                        firebase.firestore.Timestamp.fromDate(
                                            new Date(),
                                        ),
                                    updatedOn:
                                        firebase.firestore.Timestamp.fromDate(
                                            new Date(),
                                        ),
                                });
                            }

                            uploading = false;
                            progress = 0;
                            hideModal();
                        },
                        () => {
                            // Handle successful uploads on complete
                            //console.log("TRANSFER COMPLETE");
                            uploading = false;
                            progress = 100;

                            toast.push(
                                m.transcription_job_submitted_success(),
                                {
                                    classes: ["toast-success"],
                                },
                            );

                            showModal("aiTranscriptImport");
                        },
                    );
                })
                .catch((error) => {
                    console.log("Error submitting transcription job: ", error);

                    toast.push(m.transcription_error({ error: error.message }), {
                        classes: ["toast-danger"],
                    });

                    /* Update Job Record */
                    if (jobRegistration) {
                        homeRef.doc(jobInfo.id).update({
                            progress: 100,
                            statusMsg:
                                "Failed submission process. " + error.message,
                            status: "Failed",
                            completedOn: firebase.firestore.Timestamp.fromDate(
                                new Date(),
                            ),
                            updatedOn: firebase.firestore.Timestamp.fromDate(
                                new Date(),
                            ),
                        });
                    }

                    uploading = false;
                    progress = 0;
                });
        } else {
            toast.push(
                m.automatic_transcription_failed_message(),
                {
                    classes: ["toast-danger"],
                },
            );
        }
    }

    function cancelJob() {
        if (uploadTask) {
            uploadTask.cancel();
        }
    }

    function resetLanguages() {
        if (provider === "Google Speech-to-Text") {
            language = "en-US";
        } else if (provider === "ElevenLabs Scribe"){
            language = "eng";
        } else {
            language = "en";
        }
    }

    // Load KNP guides when modal opens (only for team users)
    $effect(() => {
        if (modalState.selected === 'automaticTranscription' && authState.team?.id && knpState.guides.length === 0) {
            loadKnpGuides();
        }
    });
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'automaticTranscription' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="automaticTranscriptionTitle"
    aria-describedby="automaticTranscriptionDescription"
    tabindex="-1"
    id="AutomaticTranscription"
>
    <div
        class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        role="document"
    >
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="automaticTranscriptionTitle">{m.automatic_transcription()}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label={m.close_automatic_transcription_dialog()}
                    onclick={hideModal}
                    id="closeTranscriptionBtn"
                ></button>
            </div>
            <div class="modal-body">
                <form id="transcriptionForm" aria-label={m.transcription_settings_form()}>
                    <div class="mb-3">
                        <label class="form-label" for="serviceProvider">{m.service_provider()}</label>
                        <select
                            class="form-select"
                            id="serviceProvider"
                            bind:value={provider}
                            onchange={resetLanguages}
                            aria-describedby="providerDescription"
                        >
                            <option>Speechmatics</option>
                            <option>Assembly AI</option>
                            {#if projectState.media.storage === "Local Storage" || projectState.media.storage === "Proxy RT" || projectState.media.storage === "YouTube"}
                                <option>Google Speech-to-Text</option>                                
                            {/if}
                            <option>ElevenLabs Scribe</option>
                            <option>Deepgram</option>
                            <option>Voicegain</option>
                            <option>Rev AI</option>                            
                        </select>
                    </div>

                    <!-- KNP Guide Selection (Team users only) -->
                    {#if authState.team?.id}
                        <div class="mb-3">
                            <label class="form-label" for="knpGuide">{m.knp_terminology_guide_optional()}</label>
                            <select
                                class="form-select"
                                id="knpGuide"
                                bind:value={selectedKnpGuide}
                                aria-describedby="knpDescription"
                            >
                                <option value={null}>{m.none()}</option>
                                {#each knpState.guides as guide (guide.id)}
                                    <option value={guide.id}>{guide.title}</option>
                                {/each}
                            </select>
                            <small id="knpDescription" class="form-text text-muted">
                                {m.knp_guide_description()}
                            </small>
                        </div>
                    {/if}

                    {#if provider === "Assembly AI"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(assemblyAiLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {:else if provider === "Google Speech-to-Text"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(googleLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {:else if provider === "Deepgram"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(deepgramLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {:else if provider === "Rev AI"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(revAiLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {:else if provider === "Speechmatics"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(speechmaticsLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                        {#if language === "en" || language === "cmn"}
                            <div class="mb-3">
                                <label class="form-label" for="Output Locale"
                                    >{m.output_locale()}</label
                                >
                                <select
                                    class="form-select"
                                    aria-label={m.output_locale_select()}
                                    bind:value={locale}
                                >
                                    {#if language === "en"}
                                        <!-- English -->
                                        <option value="en-GB"
                                            >{m.british_english()}</option
                                        >
                                        <option value="en-US">{m.us_english()}</option
                                        >
                                        <option value="en-AU"
                                            >{m.australian_english()}</option
                                        >
                                    {:else if language === "cmn"}
                                        <!-- Chinese -->
                                        <option value="cmn-Hans"
                                            >{m.simplified_chinese()}</option
                                        >
                                        <option value="cmn-Hant"
                                            >{m.traditional_chinese()}</option
                                        >
                                    {/if}
                                </select>
                            </div>
                        {/if}
                        {#if language === "es"}
                            <div class="mb-3">
                                <label class="form-label" for="domain"
                                    >{m.domain()}</label
                                >
                                <select class="form-select" bind:value={domain}>
                                    <option value="none">{m.none_option()}</option>
                                    <option value="bilingual-en"
                                        >{m.bilingual_english()}</option
                                    >
                                </select>
                            </div>
                        {/if}
                        {#if language === "en"}
                            <div class="mb-3">
                                <p class="small text-warning">
                                    {m.speechmatics_translation_info()}
                                </p>
                                <div class="form-check form-switch">
                                    <input
                                        class="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="enableTranslationSpeechmaticsSwitch"
                                        bind:checked={enableTranslation}
                                    />
                                    <label
                                        class="form-check-label"
                                        for="enableTranslationSpeechmaticsSwitch"
                                        >{m.enable_translation()}</label
                                    >
                                </div>
                            </div>
                            {#if enableTranslation}
                            <div class="mb-3">
                                <label class="form-label" for="target language"
                                    >{m.target_languages()}</label
                                >
                                <select
                                    class="form-select"
                                    aria-label={m.target_language_select()}
                                    multiple
                                    size="10"
                                    disabled={!enableTranslation ||
                                        language !== "en"}
                                    bind:value={trgLanguages}
                                >
                                    <option value="bg">{m.speechmatics_target_language_bulgarian()}</option>
                                    <option value="ca">{m.speechmatics_target_language_catalan()}</option>
                                    <option value="cmn">{m.speechmatics_target_language_mandarin()}</option>
                                    <option value="cs">{m.speechmatics_target_language_czech()}</option>
                                    <option value="da">{m.speechmatics_target_language_danish()}</option>
                                    <option value="de">{m.speechmatics_target_language_german()}</option>
                                    <option value="el">{m.speechmatics_target_language_greek()}</option>
                                    <option value="es">{m.speechmatics_target_language_spanish()}</option>
                                    <option value="et">{m.speechmatics_target_language_estonian()}</option>
                                    <option value="fi">{m.speechmatics_target_language_finnish()}</option>
                                    <option value="fr">{m.speechmatics_target_language_french()}</option>
                                    <option value="gl">{m.speechmatics_target_language_galician()}</option>
                                    <option value="hi">{m.speechmatics_target_language_hindi()}</option>
                                    <option value="hr">{m.speechmatics_target_language_croatian()}</option>
                                    <option value="hu">{m.speechmatics_target_language_hungarian()}</option>
                                    <option value="id">{m.speechmatics_target_language_indonesian()}</option>
                                    <option value="it">{m.speechmatics_target_language_italian()}</option>
                                    <option value="ja">{m.speechmatics_target_language_japanese()}</option>
                                    <option value="ko">{m.speechmatics_target_language_korean()}</option>
                                    <option value="lt">{m.speechmatics_target_language_lithuanian()}</option>
                                    <option value="lv">{m.speechmatics_target_language_latvian()}</option>
                                    <option value="ms">{m.speechmatics_target_language_malay()}</option>
                                    <option value="nl">{m.speechmatics_target_language_dutch()}</option>
                                    <option value="no">{m.speechmatics_target_language_norwegian()}</option>
                                    <option value="pl">{m.speechmatics_target_language_polish()}</option>
                                    <option value="pt">{m.speechmatics_target_language_portuguese()}</option>
                                    <option value="ro">{m.speechmatics_target_language_romanian()}</option>
                                    <option value="ru">{m.speechmatics_target_language_russian()}</option>
                                    <option value="sk">{m.speechmatics_target_language_slovakian()}</option>
                                    <option value="sl">{m.speechmatics_target_language_slovenian()}</option>
                                    <option value="sv">{m.speechmatics_target_language_swedish()}</option>
                                    <option value="tr">{m.speechmatics_target_language_turkish()}</option>
                                    <option value="uk">{m.speechmatics_target_language_ukrainian()}</option>
                                    <option value="vi">{m.speechmatics_target_language_vietnamese()}</option>
                                </select>
                                <p class="small text-muted">
                                    {m.additional_cost_per_minute({
                                        cost: (0.05 * trgLanguages.length).toFixed(2),
                                        languages: trgLanguages.join(),
                                    })}
                                </p>
                                <hr />
                            </div>
                            {/if}
                        {/if}
                    {:else if provider === "Voicegain"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(voiceGainLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {:else if provider === "ElevenLabs Scribe"}
                        <div class="mb-3">
                            <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                            <select
                                class="form-select"
                                id="sourceLanguage"
                                disabled={uploading}
                                bind:value={language}
                                aria-describedby="languageDescription"
                            >
                                {#each Object.entries(elevenLabsScribeLanguages) as languageOption (languageOption[0])}
                                    <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            </select>
                        </div>
                    {/if}
                </form>
                {#if language === 'multi'}
                    <div class="alert alert-info" role="alert">
                        <i class="bi bi-info-circle-fill me-2"></i>
                        {m.multi_language_support_info()}
                    </div>
                {/if}
                {#if uploading}
                    <div class="progress float-start mt-2 w-75" role="progressbar" 
                             aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}
                             aria-label={m.upload_progress_label()}>
                        <div
                            class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                            style="width: {progress}%;"
                        >
                            {statusMsg} - {progress}%
                        </div>
                    </div>
                {/if}
                <button
                    class="btn btn-light float-end ms-2"
                    type="button"
                    id="cancelUploadBtn"
                    disabled={!uploading}
                    aria-label={m.cancel_file_upload()}
                    onclick={cancelJob}>{m.cancel()}</button
                >
                <button
                    class="btn btn-primary float-end"
                    type="button"
                    id="submitJobBtn"
                    disabled={uploading}
                    aria-label={m.submit_transcription_job()}
                    onclick={() =>
                        environment.electron &&
                        (projectState.media.storage === "Local Storage" ||
                            projectState.media.storage === "Proxy RT")
                            ? extractAudio()
                            : submitJob()}>{m.submit_transcription_job()}</button
                >
                {#if uploading}
                    <p class="small text-muted text-center" role="status" aria-live="polite">{statusMsg}</p>
                    <p class="text-warning small" role="alert">
                        <i class="bi bi-exclamation-diamond-fill" aria-hidden="true"></i> 
                        {m.keep_window_open_until_upload_complete()}
                    </p>
                {/if}
            </div>
        </div>
    </div>
</div>
